import React, { useState, useEffect } from 'react';
import { logger } from './lib/logger';
import { X, Link2, Mail, Copy, Check, Trash2, Users } from 'lucide-react';
import supabase from './supabase';
import { getRoleBadge } from './types/sharingUtils';
import { useT } from './i18n/index';
import type { UserRole } from './types';

interface ShareProjectDialogProps {
  projectId: string;
  projectName: string;
  projectColor: string;
  user: { id: string; email?: string };
  onClose: () => void;
}

interface UserAccessInfo {
  id: string;
  user_id: string;
  email?: string;
  role: UserRole;
  granted_at: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: UserRole;
  created_at: string;
}

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, opacity: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export default function ShareProjectDialog({ 
  projectId, 
  projectName,
  projectColor,
  user, 
  onClose
}: ShareProjectDialogProps): JSX.Element {
  const { t } = useT();
  const [linkSharingEnabled, setLinkSharingEnabled] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [linkAccessRole, setLinkAccessRole] = useState<UserRole>('viewer');
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('viewer');
  const [users, setUsers] = useState<UserAccessInfo[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);

  const bgColor = hexToRgba(projectColor, 0.08); // 8% opacity

  useEffect(() => {
    loadShareSettings();
    loadUsers();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadShareSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('project_share_settings')
        .select('*')
        .eq('project_id', projectId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setLinkSharingEnabled(data.link_sharing_enabled);
        setLinkAccessRole(data.link_access_role);
        setShareLink(`${window.location.origin}/project/${projectId}?token=${data.share_token}`);
      }
    } catch (error) {
      logger.error('Error loading share settings:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Get all users with access
      const { data: accessData, error: accessError } = await supabase
        .from('project_access')
        .select('*')
        .eq('project_id', projectId);

      if (accessError) throw accessError;

      // Batch-fetch emails from user_profiles
      const userIds = (accessData || []).map((a: any) => a.user_id);
      const { data: profiles } = userIds.length > 0
        ? await supabase.from('user_profiles').select('id, email').in('id', userIds)
        : { data: [] };

      const emailMap: Record<string, string> = {};
      (profiles || []).forEach((p: any) => { emailMap[p.id] = p.email; });

      const usersWithEmails = (accessData || []).map((access: any) => ({
        ...access,
        email: emailMap[access.user_id] ?? access.user_id,
      })) as UserAccessInfo[];

      setUsers(usersWithEmails);

      // Pending invitations (only visible to owners via RLS)
      const { data: inviteData } = await supabase
        .from('project_invitations')
        .select('id, email, role, created_at')
        .eq('project_id', projectId)
        .eq('status', 'pending');

      // Filter out invites for emails that are already in the accepted list
      const acceptedEmails = new Set(usersWithEmails.map(u => u.email?.toLowerCase()));
      const pending = (inviteData || []).filter(
        (inv: any) => !acceptedEmails.has(inv.email?.toLowerCase())
      ) as PendingInvite[];
      setPendingInvites(pending);
    } catch (error) {
      logger.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLinkSharing = async () => {
    try {
      const newState = !linkSharingEnabled;
      
      const { error } = await supabase
        .from('project_share_settings')
        .update({ link_sharing_enabled: newState })
        .eq('project_id', projectId);

      if (error) throw error;

      setLinkSharingEnabled(newState);
    } catch (error) {
      logger.error('Error toggling link sharing:', error);
      alert(t('failedUpdateLinkSharing'));
    }
  };

  const updateLinkAccessRole = async (role: UserRole) => {
    try {
      const { error } = await supabase
        .from('project_share_settings')
        .update({ link_access_role: role })
        .eq('project_id', projectId);

      if (error) throw error;

      setLinkAccessRole(role);
    } catch (error) {
      logger.error('Error updating link access role:', error);
      alert(t('failedUpdateAccessRole'));
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) {
      alert(t('enterEmailAddress'));
      return;
    }

    if (!inviteEmail.includes('@')) {
      alert(t('enterValidEmail'));
      return;
    }

    try {
      setLoading(true);

      // Generate invite token
      const inviteToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      const { data, error } = await supabase
        .from('project_invitations')
        .insert({
          project_id: projectId,
          email: inviteEmail.trim(),
          role: inviteRole,
          invite_token: inviteToken,
          invited_by: user.id,
          status: 'pending',
        })
        .select('id')
        .single();

      if (error) throw error;

      // Call Edge Function to send the email
      const { error: fnError } = await supabase.functions.invoke('send-invitation', {
        body: { invitationId: data.id, type: 'project' },
      });

      if (fnError) {
        logger.error('Failed to send invitation email:', fnError);
      }

      setInviteEmail('');
      await loadUsers(); // refresh so the pending invite is immediately visible
    } catch (error) {
      logger.error('Error sending invite:', error);
      alert(t('failedSendInvitation'));
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (accessId: string) => {
    if (!window.confirm(t('confirmRemoveUserProject'))) return;

    try {
      const { error } = await supabase
        .from('project_access')
        .delete()
        .eq('id', accessId);

      if (error) throw error;

      await loadUsers();
    } catch (error) {
      logger.error('Error removing user:', error);
      alert(t('failedRemoveUserProject'));
    }
  };

  const updateUserRole = async (accessId: string, newRole: UserRole) => {
    try {
      const { error } = await supabase
        .from('project_access')
        .update({ role: newRole })
        .eq('id', accessId);

      if (error) throw error;

      await loadUsers();
    } catch (error) {
      logger.error('Error updating user role:', error);
      alert(t('failedUpdateUserRole'));
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(0,0,0,0.18)', animation: 'modalSlideIn 0.18s ease-out' }}
      >
        {/* Header */}
        <div style={{ position: 'sticky', top: 0, borderBottom: '1px solid #e5e7eb', padding: '20px 24px', zIndex: 10, background: bgColor }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users size={24} className="text-gray-700" />
              <div>
                <h2 className="text-xl font-bold text-gray-800">{t('shareProjectTitle')}</h2>
                <p className="text-sm text-gray-600">{projectName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6" style={{ backgroundColor: '#ffffff' }}>
          {/* Share Link Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Link2 size={20} className="text-blue-600" />
                <h3 className="font-semibold text-gray-800">{t('shareLink')}</h3>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={linkSharingEnabled}
                  onChange={toggleLinkSharing}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {linkSharingEnabled ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  {t('anyoneWithLinkProject')}
                </p>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={copyShareLink}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? t('copied') : t('copy')}
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{t('linkAccess')}</span>
                  <select
                    value={linkAccessRole}
                    onChange={(e) => updateLinkAccessRole(e.target.value as UserRole)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="viewer">{t('canView')}</option>
                    <option value="editor">{t('canEdit')}</option>
                  </select>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                {t('enableLinkSharingProjectHint')}
              </p>
            )}
          </div>

          {/* Email Invite Section */}
          <div className="border border-gray-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={20} className="text-gray-600" />
              <h3 className="font-semibold text-gray-800">{t('inviteByEmail')}</h3>
            </div>

            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={t('colleaguePlaceholder')}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && sendInvite()}
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as UserRole)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                >
                  <option value="viewer">{t('viewer')}</option>
                  <option value="editor">{t('editor')}</option>
                  <option value="owner">{t('owner')}</option>
                </select>
                <button
                  onClick={sendInvite}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  {t('invite')}
                </button>
              </div>
            </div>
          </div>

          {/* People with Access */}
          <div className="border border-gray-200 rounded-lg p-5">
            <h3 className="font-semibold text-gray-800 mb-4">{t('peopleWithAccess')}</h3>

            {loading ? (
              <div className="text-center py-4 text-gray-500">{t('loading')}</div>
            ) : users.length === 0 && pendingInvites.length === 0 ? (
              <div className="text-center py-4 text-gray-500">{t('noUsersYet')}</div>
            ) : (
              <div className="space-y-2">
                {/* Pending invitations */}
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-yellow-600">
                          {invite.email?.charAt(0).toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">{invite.email}</div>
                        <div className="text-xs text-yellow-600">{t('invitationPending')} · {invite.role}</div>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        await supabase.from('project_invitations').delete().eq('id', invite.id);
                        await loadUsers();
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('cancelInvitation')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}

                {/* Accepted users */}
                {users.map((userAccess) => {
                  const badge = getRoleBadge(userAccess.role);
                  const isCurrentUser = userAccess.user_id === user.id;

                  return (
                    <div
                      key={userAccess.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-blue-600">
                            {userAccess.email?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-800">
                            {userAccess.email || t('unknownUser')}
                            {isCurrentUser && (
                              <span className="ml-2 text-xs text-gray-500">{t('you')}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {userAccess.granted_at ? t('addedOn', { date: new Date(userAccess.granted_at).toLocaleDateString() }) : t('addedOn', { date: '—' })}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {isCurrentUser ? (
                          <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${badge.color}`}>
                            <span>{badge.label}</span>
                          </div>
                        ) : (
                          <>
                            <select
                              value={userAccess.role}
                              onChange={(e) => updateUserRole(userAccess.id, e.target.value as UserRole)}
                              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                            >
                              <option value="viewer">{t('viewer')}</option>
                              <option value="editor">{t('editor')}</option>
                              <option value="owner">{t('owner')}</option>
                            </select>
                            <button
                              onClick={() => removeUser(userAccess.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title={t('removeUser')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2 text-sm">{t('sharingProjectIncludes')}</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>✓ {t('includesAllFlowcharts')}</li>
              <li>✓ {t('includesFutureFlowcharts')}</li>
              <li>✓ {t('includesCreatePermission')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
